import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { publishDictionarySchema, type MetricDictionaryView, type PublishDictionaryInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatIsoDate } from '../../../lib/format';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useMetricDictionary, usePublishDictionary } from '../../admin/api/settings-api';
import styles from '../Reports.module.css';
import { METRIC_TONES } from '../metric-tones';

/**
 * The metric dictionary (Q19, D12): "the first time a recruiter disputes their time-to-fill
 * number, the published definition is your defense". Every figure on RPT-01 links here.
 * ADMIN-14 (S31, DECISIONS-LOG S29–S32 Q10): the HR Lead edits the wording, and each publish
 * is a new dated version that every report then names. What a metric counts is code, not text.
 */
export function MetricDictionaryPage(): ReactNode {
  const canView = useCanView('reports_own');
  const dictionary = useMetricDictionary();
  const [isEditing, setIsEditing] = useState(false);
  if (!canView) return <ForbiddenState resource="reports" contact="Your admin" />;
  if (dictionary.isPending) return <SkeletonLines count={12} />;
  if (dictionary.error) return <ServerErrorState onRetry={() => void dictionary.refetch()} />;
  const view = dictionary.data;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Metric definitions</h1>
          <p className={styles.lead}>
            Version {view.version}, published {formatIsoDate(view.publishedOn)}. Every report says which version it was computed under. HR owns these
            definitions (D12).
          </p>
        </div>
        {view.can.publish && !isEditing && (
          <div className={styles.headerActions}>
            <Button variant="outline" icon={Pencil} onClick={() => setIsEditing(true)}>
              Edit wording
            </Button>
          </div>
        )}
      </header>
      {isEditing ? <Editor key={view.version} view={view} onDone={() => setIsEditing(false)} /> : <Definitions view={view} />}
    </div>
  );
}

function Definitions({ view }: { readonly view: MetricDictionaryView }): ReactNode {
  return (
    <div className={styles.definitions}>
      {view.definitions.map((metric) => (
        <div key={metric.key} id={metric.key} className={styles.section}>
          <Card title={metric.name} tone={METRIC_TONES[metric.key]}>
            <p className={styles.definitionText}>{metric.definition}</p>
            <dl className={styles.facts}>
              {metric.start && (
                <>
                  <dt>Starts</dt>
                  <dd>{plain(metric.start)}</dd>
                </>
              )}
              {metric.stop && (
                <>
                  <dt>Stops</dt>
                  <dd>{plain(metric.stop)}</dd>
                </>
              )}
              <dt>Left out</dt>
              <dd>{metric.exclusions}</dd>
            </dl>
          </Card>
        </div>
      ))}
      {view.history.length > 0 && (
        <Card title="Versions">
          <ul className={styles.rows}>
            {view.history.map((entry) => (
              <li key={entry.version} className={styles.row}>
                <span>
                  <strong>Version {entry.version}</strong> · {formatIsoDate(entry.publishedOn)}
                  {entry.publishedByName ? ` · ${entry.publishedByName}` : ''}
                </span>
                <span className={styles.meta}>{entry.note}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Editor({ view, onDone }: { readonly view: MetricDictionaryView; readonly onDone: () => void }): ReactNode {
  const publish = usePublishDictionary();
  const show = useToasts((state) => state.show);
  const form = useForm<PublishDictionaryInput>({
    resolver: zodResolver(publishDictionarySchema),
    mode: 'onBlur',
    defaultValues: {
      note: '',
      baseVersion: view.version,
      definitions: view.definitions.map((metric) => ({ key: metric.key, name: metric.name, definition: metric.definition, exclusions: metric.exclusions })),
    },
  });
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      const published = await publish.mutateAsync(values);
      show({ tone: 'success', message: `Version ${published.version} published`, detail: 'Reports name this version from now on.' });
      onDone();
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't publish the definitions", detail: errorMessage(cause) });
    }
  });

  return (
    <form className={styles.definitions} onSubmit={(event) => void submit(event)}>
      {view.definitions.map((metric, index) => (
        <div key={metric.key} className={styles.section}>
          <Card title={metric.name} tone={METRIC_TONES[metric.key]}>
            <div className={styles.formGrid}>
              <Field label="Name" error={errors.definitions?.[index]?.name?.message}>
                {({ inputId, isInvalid }) => <Input id={inputId} isInvalid={isInvalid} maxLength={120} {...form.register(`definitions.${index}.name`)} />}
              </Field>
              <Field label="Definition" error={errors.definitions?.[index]?.definition?.message}>
                {({ inputId, isInvalid }) => <Textarea id={inputId} isInvalid={isInvalid} rows={3} maxLength={2000} {...form.register(`definitions.${index}.definition`)} />}
              </Field>
              <Field label="Left out" error={errors.definitions?.[index]?.exclusions?.message}>
                {({ inputId, isInvalid }) => <Textarea id={inputId} isInvalid={isInvalid} rows={2} maxLength={1000} {...form.register(`definitions.${index}.exclusions`)} />}
              </Field>
            </div>
          </Card>
        </div>
      ))}
      <Card title="Publish">
        <Field label="What changed" isRequired hint="Shown in the version history" error={errors.note?.message}>
          {({ inputId, describedBy, isInvalid }) => <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={500} placeholder="e.g. Clarified that internal moves are not counted" {...form.register('note')} />}
        </Field>
        <div className={styles.headerActions}>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button variant="solid" type="submit" isPending={publish.isPending} disabled={!form.formState.isDirty}>
            Publish version {view.version + 1}
          </Button>
        </div>
      </Card>
    </form>
  );
}

/** The dictionary marks event names as code; on the page they read as plain words. */
function plain(text: string): string {
  return text.replaceAll('`', '');
}
